import type { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { createGraphQLRequest } from "../../../api/utils/httpRequest"
import type {
    FoodmeowStoreResponse,
    Location,
    LocationDetail,
    LocationSaveRequestData,
    LocationsListItem,
    MutationGenerateAggregatorEmailArgs,
    MutationLinkFoodmeowStoreArgs,
    MutationLinkUberStoreArgs,
    QueryFoodmeowStoresByAuthCodeArgs,
    QueryUberStoresArgs,
    QueryUberStoresByAuthCodeArgs,
    ResponseData,
    ServiceDetail,
    TerminalAddRequestData,
    TerminalAddResponseData,
    TerminalDeactivateRequestData,
    TerminalUpdateRequestData,
    UberStoreResponse,
} from "../../../graphql/types"
import { RemoveIntegrationRequestData } from "../../../graphql/types"
import { GQLData } from "../../../graphql/utils"

export const fetchLocationsList = (headers: Record<string, any>): Promise<LocationsListItem[]> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query locationsList {
                    locationsList {
                        id
                        name
                        timezone
                        children {
                            id
                            locationId
                            name
                            type                           
                            prefix
                            isActive
                            url
                            aggregatorType
                            appVersion
                            metadata {
                                onlineOrdering {
                                    status
                                    menuUpdateStatus
                                    priceMultiplier
                                    shouldRoundUpPrices
                                    webOrderingEnabled
                                    mobileOrderingEnabled   
                                    googleOrderingLinkEnabled 
                                    images {
                                        locationImageUrl
                                    }    
                                }
                                KDS {
                                    isExpediter
                                }
                                POS {
                                    CFD {
                                        isCFDConnectionEnabled
                                        theme {
                                            idleScreen {
                                                title
                                                description
                                                logoUrl
                                                bgImagesUrls
                                                bgImagesIntervalMs                                                                                               
                                            }
                                            orderScreen {
                                                promoImagesUrls
                                                promoImagesIntervalMs                                                
                                            }
                                        }
                                    }
                                }
                                kiosk {
                                    images {
                                        welcomeScreen {
                                            id
                                            url
                                            label
                                        }
                                        logo {
                                            id
                                            url
                                            label 
                                        }
                                    }
                                }
                                waitlist {
                                    id                
                                    selfEnrollment                
                                    confirmationRequired                
                                    selfEnrollmentAvailability                
                                    gracePeriod                
                                    minPartySize             
                                    maxPartySize
                                }                                
                            }
                            aggregatorConfig {
                                uber {
                                    storeId
                                    name
                                }
                                email {
                                    address
                                }
                            }
                        } 
                    }
                }
            `,
            operationName: "locationsList",
            variables: {},
        },
    }).then(response => {
        if (response && response.data && response.data.locationsList) {
            return response.data.locationsList
        }

        return []
    })

export const fetchLocationDetailData = (
    headers: Record<string, any>,
    locationId: string
): Promise<Maybe<LocationDetail>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query locationDetail($locationId: String!) {
                    locationDetail(locationId: $locationId) {
                        id
                        name
                        timezone
                        addressLine1
                        addressLine2
                        addressZip
                        addressCity
                        addressState
                        emailAddress
                        phoneNumber
                        websiteUrl
                        latitude
                        longitude
                        googlePlaceId
                        socialFacebook
                        socialInstagram
                        socialTwitter
                        socialTikTok
                        hooTimeSpans
                        googleBusinessProfile {
                            googleLocationName
                            connectedAt
                            reviewsSyncStatus
                            reviewsSyncStartedAt
                            reviewsSyncFinishedAt
                            notificationSetupFailed
                        }
                    }
                }
            `,
            operationName: "locationDetail",
            variables: {
                locationId: locationId,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.locationDetail) {
            return response.data.locationDetail
        }

        return null
    })

export type ServiceDetailData = GQLData<"serviceDetail", ServiceDetail>
export const serviceDetailQuery = `
    query serviceDetail($serviceId: String!) {
        serviceDetail(serviceId: $serviceId) {
            id
            locationId
            name
            type
            prefix
            isActive
            kioskCustomerInstructions
            url          
            appVersion 
            aggregatorConfig {
                type
                uber {
                    storeId
                    name
                }
                email {
                    address
                }
                doordash {
                    address
                }
                grubhub {
                    address
                }
                slice {
                    address
                }
                figure {
                    storeId
                    storeName
                    logo
                    description
                    images
                }
                westSideEats {
                    apiKey
                }
            }
            metadata {
                onlineOrdering {
                    status
                    menuUpdateStatus
                    priceMultiplier
                    shouldRoundUpPrices
                    webOrderingEnabled
                    mobileOrderingEnabled
                    googleOrderingLinkEnabled
                    images {
                        locationImageUrl
                    }
                }
                KDS {
                    isExpediter
                }
                POS {
                    CFD {
                        isCFDConnectionEnabled
                        theme {
                            idleScreen {
                                title
                                description
                                logoUrl
                                bgImagesUrls
                                bgImagesIntervalMs                                                                                               
                            }
                            orderScreen {
                                promoImagesUrls
                                promoImagesIntervalMs                                                
                            }
                        }
                    }
                }
                OSB {
                    blacklistAssignmentIds
                    readyFallOfTimeMs
                }
                kiosk {
                    images {
                        welcomeScreen {
                            id
                            url
                            label
                        }
                        logo {
                            id
                            url
                            label 
                        }
                    }
                }
                waitlist {
                    id                
                    selfEnrollment                
                    confirmationRequired                
                    selfEnrollmentAvailability                
                    gracePeriod                
                    minPartySize             
                    maxPartySize
                }
            }
            aggregatorType
        }
    }
`

export const fetchServiceDetailData = (
    headers: Record<string, any>,
    serviceId: string
): Promise<Maybe<ServiceDetail>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: serviceDetailQuery,
            operationName: "serviceDetail",
            variables: {
                serviceId: serviceId,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.serviceDetail) {
            return response.data.serviceDetail
        }

        return null
    })

export const fetchLocationsTerminals = (headers: Record<string, any>): Promise<Location[]> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query locationsTerminals {
                    locationsTerminals {
                        id
                        name
                        timezone
                        terminals {
                            id
                            locationId
                            name
                            type
                            status
                            prefix
                            domainName
                            url
                            kioskCustomerInstructions
                            slug
                            appVersion
                            metadata {
                                onlineOrdering {
                                    status
                                    menuUpdateStatus
                                    priceMultiplier
                                    shouldRoundUpPrices
                                    webOrderingEnabled
                                    mobileOrderingEnabled    
                                    googleOrderingLinkEnabled
                                    images {
                                        locationImageUrl
                                    }                                  
                                }
                                KDS {
                                    isExpediter
                                }
                                POS {
                                    CFD {
                                        isCFDConnectionEnabled
                                        theme {
                                            idleScreen {
                                                title
                                                description
                                                logoUrl
                                                bgImagesUrls
                                                bgImagesIntervalMs                                                                                               
                                            }
                                            orderScreen {
                                                promoImagesUrls
                                                promoImagesIntervalMs                                                
                                            }
                                        }
                                    }
                                }
                                kiosk {
                                    images {
                                        welcomeScreen {
                                            id
                                            url
                                            label
                                        }
                                        logo {
                                            id
                                            url
                                            label 
                                        }
                                    }
                                }
                                waitlist {
                                    id                
                                    selfEnrollment                
                                    confirmationRequired                
                                    selfEnrollmentAvailability                
                                    gracePeriod                
                                    minPartySize             
                                    maxPartySize
                                }                                
                            }
                            aggregatorConfig {
                                type
                                uber {
                                    storeId
                                    name
                                }
                                email {
                                    address
                                }
                                doordash {
                                    address
                                }
                                grubhub {
                                    address
                                }
                                slice {
                                    address
                                }
                                figure {
                                    storeId
                                    storeName
                                    logo
                                    description
                                    images
                                }
                                westSideEats {
                                    apiKey
                                }
                            }                                                
                        }
                        addressLine1
                        addressLine2
                        addressZip
                        addressCity
                        addressState
                        emailAddress
                        phoneNumber
                        websiteUrl
                        latitude
                        longitude
                        googlePlaceId
                        socialFacebook
                        socialInstagram
                        socialTwitter
                        socialTikTok
                        googleBusinessProfile {
                            googleLocationName
                            connectedAt
                            reviewsSyncStatus
                            reviewsSyncStartedAt
                            reviewsSyncFinishedAt
                            notificationSetupFailed
                        }
                    }
                }
            `,
            operationName: "locationsTerminals",
            variables: {},
        },
    }).then(response => {
        if (response && response.data && response.data.locationsTerminals) {
            return response.data.locationsTerminals
        }

        return []
    })

export const postLocationSaveMutation = (
    headers: Record<string, any>,
    data: LocationSaveRequestData
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation locationSave($data: LocationSaveRequestData!) {
                    locationSave(data: $data) {
                      success
                    }
                }
            `,
            operationName: "locationSave",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        return response.locationSave
    })

export const postLocationDeleteMutation = (headers: Record<string, any>, id: string): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation locationDelete($locationId: String!) {
                    locationDelete(locationId: $locationId) {
                      success
                    }
                }
            `,
            operationName: "locationDelete",
            variables: {
                locationId: id,
            },
        },
    }).then(response => {
        return response.locationDelete
    })

export const postTerminalAddMutation = (
    headers: Record<string, any>,
    data: TerminalAddRequestData
): Promise<TerminalAddResponseData> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation terminalAdd($data: TerminalAddRequestData!) {
                    terminalAdd(data: $data) {
                      terminalPrefix
                      status
                      slug
                    }
                }
            `,
            operationName: "terminalAdd",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        return response.data.terminalAdd
    })

export const postTerminalUpdateMutation = (
    headers: Record<string, any>,
    data: TerminalUpdateRequestData
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation terminalUpdate($data: TerminalUpdateRequestData!) {
                    terminalUpdate(data: $data) {
                      success
                    }
                }
            `,
            operationName: "terminalUpdate",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        return response.terminalUpdate
    })

export const terminalDeleteMutation = (headers: Record<string, any>, id: string): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation terminalDelete($terminalId: String!) {
                    terminalDelete(terminalId: $terminalId) {
                      success
                    }
                }
            `,
            operationName: "terminalDelete",
            variables: {
                terminalId: id,
            },
        },
    }).then(response => {
        return response.terminalDelete
    })

export const removeIntegrationMutation = (
    headers: Record<string, any>,
    data: RemoveIntegrationRequestData
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation removeIntegration($data: RemoveIntegrationRequestData!) {
                    removeIntegration(data: $data) {
                      success
                    }
                }
            `,
            operationName: "removeIntegration",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        return response.removeIntegration
    })

export const terminalDeactivateMutation = (
    headers: Record<string, any>,
    data: TerminalDeactivateRequestData
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation terminalDeactivate($data: TerminalDeactivateRequestData!) {
                    terminalDeactivate(data: $data) {
                      success
                    }
                }
            `,
            operationName: "terminalDeactivate",
            variables: {
                data: data,
            },
        },
    }).then(response => {
        return response.terminalDeactivate
    })

export const terminalActivateMutation = (
    headers: Record<string, any>,
    terminalId: string
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                 mutation terminalActivate($terminalId: String!) {
                    terminalActivate(terminalId: $terminalId) {
                      success
                    }
                }
            `,
            operationName: "terminalActivate",
            variables: {
                terminalId: terminalId,
            },
        },
    }).then(response => {
        return response.terminalActivate
    })

export const fetchUberStoresQueryByCode = (
    headers: Record<string, any>,
    args: QueryUberStoresByAuthCodeArgs
): Promise<UberStoreResponse> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query fetchUberStores($code: String!) {
                    uberStoresByAuthCode(code: $code) {
                        uberAuth {
                            accessToken
                            refreshToken
                            expiresIn
                        }
                        stores {
                            storeId
                            name
                        }
                    }
                }
            `,
            variables: args,
        },
    }).then(response => response.data.uberStoresByAuthCode)

export const fetchUberStoresQuery = async (
    headers: Record<string, any>,
    args: QueryUberStoresArgs
): Promise<UberStoreResponse> => {
    const { data } = await createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query fetchUberStores($serviceId: String!) {
                    uberStores(serviceId: $serviceId) {
                        stores {
                            storeId
                            name
                        }
                    }
                }
            `,
            variables: args,
        },
    })
    return data.uberStores
}

export const fetchFoodmeowStoresQuery = (
    headers: Record<string, any>,
    args: QueryFoodmeowStoresByAuthCodeArgs
): Promise<FoodmeowStoreResponse> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query fetchFoodmeowStores($code: String!) {
                    foodmeowStoresByAuthCode(code: $code) {
                        token
                        stores {
                            storeId
                            name
                        }
                    }
                }
            `,
            variables: args,
        },
    }).then(response => response.data.foodmeowStoresByAuthCode)

export const linkUberStore = (
    headers: Record<string, any>,
    args: MutationLinkUberStoreArgs
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                mutation linkUberStore($auth: OAuthTokensInput!, $storeId: String!, $locationId: String!, $terminalId: String!) {
                    linkUberStore(auth: $auth, storeId: $storeId, locationId: $locationId, terminalId: $terminalId) {
                        success
                    }
                }
            `,
            variables: args,
        },
    }).then(response => response.data.linkUberStore)

export const linkFoodmeowStore = (
    headers: Record<string, any>,
    args: MutationLinkFoodmeowStoreArgs
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                mutation linkFoodmeowStore($token: String!, $storeId: String!, $locationId: String!, $terminalId: String!) {
                    linkFoodmeowStore(token: $token, storeId: $storeId, locationId: $locationId, terminalId: $terminalId) {
                        success
                    }
                }
            `,
            variables: args,
        },
    }).then(response => response.data.linkFoodmeowStore)

export const generateAggregatorEmail = (
    headers: Record<string, any>,
    args: MutationGenerateAggregatorEmailArgs
): Promise<Maybe<ResponseData>> =>
    createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                mutation generateAggregatorEmail($locationId: String!, $terminalId: String!, $aggregatorType: String) {
                    generateAggregatorEmail(locationId: $locationId, terminalId: $terminalId, aggregatorType: $aggregatorType) {
                        success
                    }
                }
            `,
            variables: args,
        },
    }).then(response => response.data.generateAggregatorEmail)
